/**
 * ATL-1445 stage 3, P6 — the stock effects of a sale, extracted from `saveNewSale`'s batch loop so the
 * Firestore batch and the local view are computed by THE SAME CODE (plan §3: "Effects are computed by
 * the same code that builds today's Firestore batch … so the overlay cannot drift from what Firestore
 * mode shows").
 *
 * `saleStockLines` is master's loop with its two writes taken out: it decides, line by line and in the
 * same order, WHICH product takes WHAT increment — composite expansion at sale time, the
 * `manufacturedBeforeSale` branch, the two pseudo-line sentinels, the `INGREDIENT_NOT_FOUND` refusal
 * and the two diagnostics. `saveNewSale` turns each yielded line into its `batch.update` + movement
 * `batch.set` exactly as before; `saleStockEffects` turns the same lines into the local view's deltas.
 *
 * IT IS A GENERATOR ON PURPOSE. Master's loop interleaves its validation with its writes: line 1's
 * `getFirestoreRef` (which can throw) runs before line 2's ingredient check. A function that validated
 * the whole cart first would change WHICH error a malformed cart throws. Yielding one line at a time
 * keeps every throw and every `console.warn`/`console.error` at the exact point master had it, between
 * the same two batch calls — `saleStockEffects.differential.test.js` drives master's frozen loop and this
 * one over a corpus and compares the recorded batch call for call, errors included.
 *
 * Pure: no Firestore, no atoms, no I/O. The products dict is passed in by the writer (who read it once).
 */
import type { StockDeltaEffect } from '../Write/localEffects'

/* eslint-disable @typescript-eslint/no-explicit-any -- cart lines and the products dict are `any` in saveNewSale, which this mirrors */

export interface SaleStockLine {
  /** The products dict key (the cart line's / ingredient's `id`): the movement's `productID`. */
  productId: string
  /** `0 - quantity` (simple line) or `0 - (quantity × ingredient quantity)` (composite at sale time). */
  increment: number
  /** What `getFirestoreRef` resolves the stock document from: the dict's product, else the line. */
  refSource: any
}

/**
 * The stock lines of a sale, in batch order. Throws `INGREDIENT_NOT_FOUND` (with `ingredientName` and
 * `productName`) when it reaches a composite ingredient missing from the dict — at that point in the
 * iteration, as master's loop did.
 */
export function* saleStockLines(info: any, allProductsDict: Record<string, any>): Generator<SaleStockLine, void, undefined> {
  for (const value of info.cartItems) {
    if (value.isComposite && !value.manufacturedBeforeSale) {
      for (const ingredient of value.ingredients) {
        if (!allProductsDict[ingredient.id]) {
          const error: any = new Error('INGREDIENT_NOT_FOUND')
          error.ingredientName = ingredient.name
          error.productName = value.product?.name
          throw error
        }
        const increment = 0 - (value.quantity * ingredient.quantity)
        yield { productId: ingredient.id, increment, refSource: allProductsDict[ingredient.id] || ingredient }
      }
    } else {
      if (value.id !== 'customProduct' && value.id !== 'delivery') {
        const increment = 0 - value.quantity
        // Get product from dict (keyed by Supabase UUID) to get firestoreId
        const productFromDict = allProductsDict[value.id]
        if (!productFromDict) {
          console.warn(`[SAVE_NEW_SALE] Product not found in dict: ${value.id}, name: ${value.name}`)
        }
        if (!productFromDict?.firestoreId && !value.firestoreId) {
          console.error(`[SAVE_NEW_SALE] No firestoreId for product: ${value.id}, name: ${value.name}`)
        }
        yield { productId: value.id, increment, refSource: productFromDict || value }
      }
    }
  }
}

/**
 * The local view's stock deltas for a sale: one per line `saveNewSale` would write, same product,
 * same store, same increment, same order. Throws exactly what `saleStockLines` throws.
 */
export function saleStockEffects(info: any, allProductsDict: Record<string, any>): StockDeltaEffect[] {
  const effects: StockDeltaEffect[] = []
  for (const line of saleStockLines(info, allProductsDict)) {
    effects.push({ productId: line.productId, storeId: info.store, delta: line.increment })
  }
  return effects
}
